/**
 * Reusable "Create Payment Link" modal.
 * Can be embedded in any page — prefill name/email/phone from the source record.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   prefill    { name?, email?, phone?, description? }
 *   onSuccess  (result) => void   optional — called after successful creation
 */
import { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import FlexibleLinkFields from "components/FlexibleLinkFields";

import { adminApi } from "services/api";
import { lightDark, darkTint, DARK } from "utils/lightDark";

// The modal's accent colours and their dark-mode partners (the light ones vanish on navy).
const INDIGO_TEXT = lightDark("#1a237e", "#9fa8da");
const SCHEDULE_BORDER = lightDark("1px solid #c5cae9", `1px solid ${DARK.border}`);

// ---------------------------------------------------------------------------
const CURRENCIES = ["NGN", "USD", "GBP"];

const COUNTRIES = [
  "Nigeria",
  "United Kingdom",
  "United States",
  "Ghana",
  "Kenya",
  "South Africa",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Other",
];

function gatewayForCurrency(currency) {
  return currency === "NGN" ? "paystack" : "stripe";
}

function fmt(amount, currency) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);
  } catch {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
}

const today = new Date().toISOString().split("T")[0];

// Cycle 1 is always due "now" (whenever the customer pays), so the 2nd instalment
// (startDate) must land strictly after today — otherwise the schedule shows both
// cycles on the same date, and Paystack will reject a subscription start_date that
// isn't in the future.
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const minStartDate = tomorrow.toISOString().split("T")[0];

// Mirrors the server's CADENCE table (routes/generated-payment-links.js) — 'biweekly'
// is Stripe-only (Paystack has no native "every 2 weeks" plan interval); the rest map
// 1:1 onto both gateways.
const CADENCE = {
  daily: { unit: "day", amount: 1, label: "day", option: "Daily" },
  weekly: { unit: "day", amount: 7, label: "week", option: "Weekly" },
  biweekly: { unit: "day", amount: 14, label: "2 weeks", option: "Biweekly", stripeOnly: true },
  monthly: { unit: "month", amount: 1, label: "month", option: "Monthly" },
  quarterly: { unit: "month", amount: 3, label: "3 months", option: "Quarterly (every 3 months)" },
  biannually: {
    unit: "month",
    amount: 6,
    label: "6 months",
    option: "Biannually (every 6 months)",
  },
  annually: { unit: "month", amount: 12, label: "year", option: "Annually" },
};

function defaultStartDateFor(cadence) {
  const cfg = CADENCE[cadence] || CADENCE.monthly;
  const d = new Date();
  if (cfg.unit === "day") d.setDate(d.getDate() + cfg.amount);
  else d.setMonth(d.getMonth() + cfg.amount);
  return d.toISOString().split("T")[0];
}

function cadenceWord(cadence) {
  return (CADENCE[cadence] || CADENCE.monthly).label;
}

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  country: "",
  currency: "USD",
  amount: "",
  paymentType: "full",
  description: "",
  expiryHours: 168,
  installmentCount: 3,
  cadence: "monthly",
  startDate: defaultStartDateFor("monthly"),
  minPayment: "",
  payBy: "",
};

// ---------------------------------------------------------------------------
export default function PaymentLinkModal({ open, onClose, prefill = {}, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        name: prefill.name || "",
        email: prefill.email || "",
        phone: prefill.phone || "",
        description: prefill.description || "",
      });
      setError(null);
      setResult(null);
    }
  }, [open, prefill.name, prefill.email, prefill.phone, prefill.description]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  // Some cadences (currently just biweekly) only exist on Stripe — switching currency
  // to NGN while one is selected would otherwise silently produce a combination the
  // server rejects.
  function handleCurrencyChange(e) {
    const newCurrency = e.target.value;
    setForm((f) => {
      const stillCompatible = !(
        gatewayForCurrency(newCurrency) === "paystack" && CADENCE[f.cadence]?.stripeOnly
      );
      const cadence = stillCompatible ? f.cadence : "weekly";
      return {
        ...f,
        currency: newCurrency,
        cadence,
        startDate: stillCompatible ? f.startDate : defaultStartDateFor(cadence),
      };
    });
  }

  // Switching cadence re-suggests a startDate — but only if the admin hasn't
  // already customised it away from the previous cadence's auto-default.
  function handleCadenceChange(e) {
    const newCadence = e.target.value;
    setForm((f) => ({
      ...f,
      cadence: newCadence,
      startDate:
        f.startDate === defaultStartDateFor(f.cadence)
          ? defaultStartDateFor(newCadence)
          : f.startDate,
    }));
  }

  const gateway = gatewayForCurrency(form.currency);

  // Cent-based schedule preview — mirrors server logic exactly. Cycle 1 is due now
  // (paid through the customer's page); cycles 2+ step by `cadence` from `startDate`.
  const schedulePreview = useMemo(() => {
    if (form.paymentType !== "installment") return [];
    const count = parseInt(form.installmentCount);
    const amount = parseFloat(form.amount);
    if (!count || count < 2 || !form.startDate || !amount) return [];
    const start = new Date(form.startDate);
    const totalCents = Math.round(amount * 100);
    const baseCents = Math.floor(totalCents / count);
    const remainderCents = totalCents - baseCents * count;
    return Array.from({ length: count }, (_, i) => {
      const d = i === 0 ? new Date() : new Date(start);
      if (i > 0) {
        const cfg = CADENCE[form.cadence] || CADENCE.monthly;
        if (cfg.unit === "day") d.setDate(d.getDate() + cfg.amount * (i - 1));
        else d.setMonth(d.getMonth() + cfg.amount * (i - 1));
      }
      const instCents = baseCents + (i === 0 ? remainderCents : 0);
      return { index: i + 1, dueDate: d, amount: instCents / 100 };
    });
  }, [form.paymentType, form.installmentCount, form.cadence, form.startDate, form.amount]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (form.paymentType === "installment" && form.startDate <= today) {
      setError("Start Date (2nd instalment) must be after today — cycle 1 is always due now.");
      return;
    }
    if (
      form.paymentType === "installment" &&
      CADENCE[form.cadence]?.stripeOnly &&
      gatewayForCurrency(form.currency) === "paystack"
    ) {
      setError(
        `${CADENCE[form.cadence].option} cadence is only available for Stripe (non-NGN) currencies.`
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        country: form.country.trim(),
        amount: parseFloat(form.amount),
        currency: form.currency,
        paymentType: form.paymentType,
        description: form.description.trim(),
        expiryHours: parseInt(form.expiryHours) || 168,
      };
      if (form.paymentType === "installment") {
        payload.installmentCount = parseInt(form.installmentCount);
        payload.cadence = form.cadence;
        payload.startDate = form.startDate;
      }
      if (form.paymentType === "flexible") {
        if (form.minPayment !== "") payload.minPayment = parseFloat(form.minPayment);
        if (form.payBy) payload.payBy = form.payBy;
      }
      const res = await adminApi.generatePaymentLink(payload);
      setResult(res);
      if (onSuccess) onSuccess(res);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to generate payment link");
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    if (!loading) onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon sx={{ color: INDIGO_TEXT }}>link</Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Create Payment Link
          </MDTypography>
        </MDBox>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={loading}
          sx={{ width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Payment link created!&nbsp; Email {result.emailSent ? "✓ sent" : "✗ failed"}{" "}
              &nbsp;·&nbsp; SMS {result.smsSent ? "✓ sent" : "✗ failed"}
            </Alert>
          )}

          {/* Recipient */}
          <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
            Recipient Details
          </MDTypography>
          <Grid container spacing={1.5} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="+447911123456"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select name="country" value={form.country} label="Country" onChange={handleChange}>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* Payment details */}
          <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
            Payment Details
          </MDTypography>
          <Grid container spacing={1.5} mb={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={form.currency}
                  label="Currency"
                  onChange={handleCurrencyChange}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={form.paymentType === "flexible" ? "Total Balance" : "Amount"}
                name="amount"
                type="number"
                value={form.amount}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                inputProps={{ min: 0.01, step: "0.01" }}
              />
            </Grid>
            {form.paymentType !== "flexible" && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Expiry Hours"
                  name="expiryHours"
                  type="number"
                  value={form.expiryHours}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}
          </Grid>

          {/* Gateway badge */}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDTypography variant="caption" color="text">
              Gateway:
            </MDTypography>
            <Chip
              icon={
                <Icon sx={{ fontSize: "0.9rem !important" }}>
                  {gateway === "stripe" ? "credit_card" : "account_balance"}
                </Icon>
              }
              label={gateway === "stripe" ? "Stripe" : "Paystack"}
              size="small"
              sx={{
                background: gateway === "stripe" ? "#635bff22" : "#00c67422",
                color:
                  gateway === "stripe"
                    ? lightDark("#635bff", "#a5a0ff")
                    : lightDark("#00996a", "#4cd6a4"),
                fontWeight: 700,
                border: `1px solid ${gateway === "stripe" ? "#635bff55" : "#00c67455"}`,
              }}
            />
            <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
              {gateway === "paystack" ? "NGN → Paystack (Nigeria)" : `${form.currency} → Stripe`}
            </MDTypography>
          </MDBox>

          {/* Payment type */}
          <MDBox mb={2}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              display="block"
              mb={0.75}
            >
              Payment Type
            </MDTypography>
            <ToggleButtonGroup
              value={form.paymentType}
              exclusive
              size="small"
              onChange={(_, v) => v && setForm((f) => ({ ...f, paymentType: v }))}
            >
              <ToggleButton value="full" sx={{ px: 2.5 }}>
                <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>paid</Icon>Full Payment
              </ToggleButton>
              <ToggleButton value="installment" sx={{ px: 2.5 }}>
                <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>event_repeat</Icon>Instalment Plan
              </ToggleButton>
              <ToggleButton value="flexible" sx={{ px: 2.5 }}>
                <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>tune</Icon>Flexible Amount
              </ToggleButton>
            </ToggleButtonGroup>
          </MDBox>

          {/* Instalment fields */}
          {form.paymentType === "installment" && (
            <MDBox
              sx={{
                background: lightDark("#f0f4ff", darkTint("#1a73e8", 0.08)),
                borderRadius: 2,
                p: 1.5,
                mb: 2,
                border: SCHEDULE_BORDER,
              }}
            >
              <MDTypography variant="caption" fontWeight="bold" color="info" display="block" mb={1}>
                Instalment Settings
              </MDTypography>
              <Grid container spacing={1.5} mb={schedulePreview.length > 0 ? 1.5 : 0}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="No. of Instalments"
                    name="installmentCount"
                    type="number"
                    value={form.installmentCount}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    inputProps={{ min: 2, max: 60 }}
                    helperText="Min 2"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Cadence</InputLabel>
                    <Select
                      name="cadence"
                      value={form.cadence}
                      label="Cadence"
                      onChange={handleCadenceChange}
                    >
                      {Object.entries(CADENCE).map(([value, cfg]) => {
                        const isPaystack = gatewayForCurrency(form.currency) === "paystack";
                        const disabled = cfg.stripeOnly && isPaystack;
                        return (
                          <MenuItem key={value} value={value} disabled={disabled}>
                            {cfg.option}
                            {disabled ? " (Stripe only)" : ""}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Start Date"
                    name="startDate"
                    type="date"
                    value={form.startDate}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minStartDate }}
                    helperText="2nd instalment due date — must be after today"
                  />
                </Grid>
              </Grid>

              {/* Live schedule preview */}
              {schedulePreview.length > 0 && (
                <MDBox
                  sx={{
                    background: lightDark("#fff", DARK.surface),
                    borderRadius: 1.5,
                    border: SCHEDULE_BORDER,
                    overflow: "hidden",
                  }}
                >
                  <MDBox
                    sx={{
                      background: "#1a237e",
                      px: 1.5,
                      py: 0.75,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        letterSpacing: 0.5,
                      }}
                    >
                      SCHEDULE PREVIEW
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.65rem" }}
                    >
                      {(() => {
                        const first = schedulePreview[0]?.amount;
                        const last = schedulePreview[schedulePreview.length - 1]?.amount;
                        const amounts =
                          first === last
                            ? `${fmt(first, form.currency)} each`
                            : `${fmt(first, form.currency)} first · ${fmt(
                                last,
                                form.currency
                              )} each after`;
                        return `${amounts} · every ${cadenceWord(form.cadence)}`;
                      })()}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ maxHeight: 180, overflowY: "auto" }}>
                    {schedulePreview.map((row) => (
                      <MDBox
                        key={row.index}
                        display="flex"
                        alignItems="center"
                        px={1.5}
                        py={0.8}
                        sx={{
                          borderBottom: lightDark("1px solid #f0f0f0", `1px solid ${DARK.border}`),
                          "&:last-child": { borderBottom: "none" },
                          background:
                            row.index % 2 === 0
                              ? lightDark("#fafbff", DARK.surface)
                              : lightDark("#fff", "transparent"),
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            background: "#1a237e",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{ color: "#fff", fontSize: "0.6rem", fontWeight: 700 }}
                          >
                            {row.index}
                          </MDTypography>
                        </MDBox>
                        <MDBox flex={1}>
                          <MDTypography
                            variant="caption"
                            fontWeight={600}
                            sx={{ fontSize: "0.72rem" }}
                          >
                            {row.dueDate.toLocaleDateString("en-GB", {
                              weekday: "short",
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </MDTypography>
                        </MDBox>
                        <MDTypography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: INDIGO_TEXT, fontSize: "0.72rem" }}
                        >
                          {fmt(row.amount, form.currency)}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                  <MDBox
                    sx={{
                      background: lightDark("#f0f4ff", darkTint("#1a73e8", 0.12)),
                      px: 1.5,
                      py: 0.65,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <MDTypography variant="caption" fontWeight={700} sx={{ fontSize: "0.7rem" }}>
                      Total
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontWeight={700}
                      sx={{ fontSize: "0.7rem", color: INDIGO_TEXT }}
                    >
                      {fmt(parseFloat(form.amount) || 0, form.currency)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}

          {form.paymentType === "flexible" && (
            <FlexibleLinkFields form={form} onChange={handleChange} />
          )}

          {/* Description */}
          <TextField
            label="Description / Message to recipient"
            name="description"
            value={form.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="e.g. Payment for Full-Stack Bootcamp enrolment"
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            "& .MuiButton-root": { minHeight: { xs: 40, md: "auto" } },
          }}
        >
          <MDButton onClick={handleClose} disabled={loading} color="secondary" size="small">
            {result ? "Close" : "Cancel"}
          </MDButton>
          {!result && (
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              size="small"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={14} color="inherit" /> : <Icon>send</Icon>
              }
            >
              {loading ? "Generating…" : "Generate & Send"}
            </MDButton>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
